import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Container from "react-bootstrap/Container";
import AppNavbar from "../components/Navbar";
import Loading from "../components/Loading";
import SpacesService from "../services/providers/SpacesService";
import api from "../services/api";
import { url } from "../utils/pubUrl";
import toast from "react-hot-toast";
import { HiLockClosed, HiGlobeAlt, HiCheck } from "react-icons/hi";
import "./OrderSpace.css";
import "../styles/global.css";
import { useAuth } from "../context/AuthContext";
import PaymentModeDropdown from "../components/common/PaymentModeDropdown";
import { generateRandomSpaceName } from "../components/OrderForm/orderUtils";

const OrderSpace = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    region: "",
    storage_tier: "250GB",
    acl: "private",
  });

  const [regions, setRegions] = useState([]);
  const [storageTiers, setStorageTiers] = useState([]);
  const [pricing, setPricing] = useState(null);

  const [orderResult, setOrderResult] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);
  const [paymentProvider, setPaymentProvider] = useState("razorpay");
  const [startingPayment, setStartingPayment] = useState(false);

  // Currency selection for Spaces:
  // - URL param wins for display: /spaces/order?currency=USD
  // - Otherwise, if logged in and profile currency is USD, show USD
  // - Else default INR
  const currencyParamRaw = (searchParams.get("currency") || "").toUpperCase();
  const hasCurrencyOverride = currencyParamRaw === "USD";
  const [displayCurrency, setDisplayCurrency] = useState(
    hasCurrencyOverride ? "USD" : "INR",
  );

  useEffect(() => {
    if (hasCurrencyOverride) {
      setDisplayCurrency("USD");
      return;
    }
    const profileCurrency = (user?.currency || "").toString().toUpperCase();
    if (profileCurrency === "USD") {
      setDisplayCurrency("USD");
      return;
    }
    setDisplayCurrency("INR");
  }, [hasCurrencyOverride, user]);

  const fetchRegions = useCallback(async () => {
    try {
      const response = await api.get("/resources/regions");
      setRegions(response.data || []);
    } catch (error) {
      console.error("Error fetching regions:", error);
      toast.error("Failed to load regions");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPricing = useCallback(async () => {
    try {
      const response = await api.get("/spaces/pricing", {
        params: { currency: displayCurrency },
      });
      const pricingData = response.data;
      setPricing(pricingData);

      const tiers = (pricingData.storage_tiers || []).map((tier) => ({
        size_gb: tier.size_gb,
        price: Number(tier.display_price || 0),
        label:
          tier.label ||
          (tier.size_gb >= 1000
            ? `${tier.size_gb / 1000} TB`
            : `${tier.size_gb} GB`),
      }));

      setStorageTiers(tiers);

      if (tiers.length > 0) {
        setFormData((prev) => {
          if (!prev.storage_tier) {
            return { ...prev, storage_tier: `${tiers[0].size_gb}GB` };
          }
          return prev;
        });
      }
    } catch (error) {
      console.error("Error fetching Spaces pricing:", error);
      toast.error("Failed to load pricing information");
    }
  }, [displayCurrency]);

  useEffect(() => {
    fetchRegions();
    fetchPricing();
  }, [fetchRegions, fetchPricing]);

  // Generate random space name on mount if name is empty
  useEffect(() => {
    if (!formData.name) {
      const randomName = generateRandomSpaceName();
      setFormData((prev) => ({ ...prev, name: randomName }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set default region to "blr1" when regions are loaded (if no region selected)
  useEffect(() => {
    if (regions.length > 0 && !formData.region) {
      const blr1Region = regions.find((r) => r.slug === "sgp1");
      if (blr1Region) {
        setFormData((prev) => ({ ...prev, region: "sgp1" }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please log in to place an order.");
      return;
    }
    setSubmitting(true);

    if (!formData.name || !formData.region) {
      toast.error("Please complete all required fields");
      setSubmitting(false);
      return;
    }

    const suffix = "-s3-cyb";
    const baseName = String(formData.name || "")
      .toLowerCase()
      .trim();
    const finalName = baseName.endsWith(suffix)
      ? baseName
      : `${baseName}${suffix}`;
    if (
      !/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(baseName) ||
      baseName.length < 8 ||
      baseName.length > 63
    ) {
      toast.error(
        "Bucket name must be 8-63 characters, lowercase alphanumeric with hyphens",
      );
      setSubmitting(false);
      return;
    }
    if (finalName.length > 63) {
      toast.error(
        `Bucket name is too long. Please choose a shorter name (max ${
          63 - suffix.length
        } chars)`,
      );
      setSubmitting(false);
      return;
    }

    try {
      const result = await SpacesService.createOrder({
        ...formData,
        name: baseName,
      });
      setOrderResult(result);
      toast.success("Order created successfully!");
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to create order";
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentClick = () => {
    const orderId = orderResult?.orderId;
    if (!orderId) return;
    setStartingPayment(true);
    api
      .post("/payments/create", { orderId, provider: paymentProvider })
      .then((res) => {
        const urlToOpen = res.data?.url;
        if (urlToOpen) {
          window.location.href = urlToOpen;
        } else {
          toast.error("Payment link not available. Please try again.");
        }
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Failed to start payment");
      })
      .finally(() => setStartingPayment(false));
  };

  const getRegionIcon = (regionSlug) => {
    const regionToCountryMap = {
      nyc: "us",
      sfo: "us",
      tor: "ca",
      lon: "gb",
      ams: "nl",
      fra: "de",
      sgp: "sg",
      blr: "in",
      syd: "au",
      atl: "us",
    };
    // Extract first 3 characters to match region prefix (e.g., "blr1" -> "blr")
    const prefix = String(regionSlug || "")
      .toLowerCase()
      .substring(0, 3);
    const countryCode = regionToCountryMap[prefix];
    if (countryCode) {
      return (
        <img
          src={url(`icons/flags/${countryCode}.png`)}
          alt={regionSlug}
          className="order-region-flag"
        />
      );
    }
    return (
      <img
        src={url("icons/flags/us.png")}
        alt={regionSlug}
        className="order-region-flag"
      />
    );
  };

  const isFormValid = () => {
    return formData.name && formData.region;
  };

  const getSelectedTier = () => {
    if (!formData.storage_tier) return null;
    return storageTiers.find(
      (tier) => `${tier.size_gb}GB` === formData.storage_tier,
    );
  };

  // Pre-select storage tier based on URL size_gb, e.g. /spaces/order?size_gb=50
  useEffect(() => {
    const raw = (searchParams.get("size_gb") || "").trim();
    if (!raw) return;
    const parsed = parseInt(raw, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    if (!Array.isArray(storageTiers) || storageTiers.length === 0) return;
    const matchedTier = storageTiers.find((t) => Number(t.size_gb) === parsed);
    if (!matchedTier) return;
    setFormData((prev) => ({
      ...prev,
      storage_tier: `${matchedTier.size_gb}GB`,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageTiers]);

  const handleLoginRedirect = () => {
    navigate("/login");
  };

  const getCurrencySymbol = () => {
    const currency = (
      orderResult?.currency ||
      pricing?.currency ||
      displayCurrency ||
      "USD"
    )
      .toString()
      .toUpperCase();
    switch (currency) {
      case "INR":
        return "₹";
      case "EUR":
        return "€";
      case "GBP":
        return "£";
      default:
        return "$";
    }
  };

  const formatMoney = (amount, currencyCode) => {
    const c = (currencyCode || "USD").toString().toUpperCase();
    const symbol =
      c === "INR" ? "₹" : c === "EUR" ? "€" : c === "GBP" ? "£" : "$";
    const n = Number(amount || 0);
    return `${symbol}${Number.isFinite(n) ? n.toFixed(2) : "0.00"}`;
  };

  if (loading) return <Loading />;

  return (
    <>
      <AppNavbar />
      <div className="order-space-wrapper">
        <Container>
          {/* Page Header */}
          <div className="order-header main-container">
            <h1 className="page-title">Create a Static Storage</h1>
            <p className="order-subtitle">
              Deploy S3-compatible object storage for your applications
            </p>
          </div>

          {/* Order Success State */}
          {orderResult ? (
            <div className="order-success-container">
              <div className="order-success">
                <div className="success-icon">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <h3 className="success-title">Order Created Successfully!</h3>
                <p className="success-description">
                  Your Space order has been created. Complete payment to
                  activate your Space.
                </p>
                <div className="success-details">
                  <div className="detail-row">
                    <span className="detail-label">Order ID</span>
                    <span className="detail-value">{orderResult.orderId}</span>
                  </div>
                  {orderResult.invoiceId ? (
                    <div className="detail-row">
                      <span className="detail-label">Invoice #</span>
                      <span className="detail-value">
                        {orderResult.invoiceNumber || orderResult.invoiceId}
                      </span>
                    </div>
                  ) : null}
                  {orderResult.total != null ? (
                    <div className="detail-row">
                      <span className="detail-label">Total</span>
                      <span className="detail-value">
                        {formatMoney(orderResult.total, orderResult.currency)}
                      </span>
                    </div>
                  ) : null}
                  {orderStatus && (
                    <div className="detail-row">
                      <span className="detail-label">Status</span>
                      <span
                        className={`badge ${
                          orderStatus === "activated"
                            ? "badge-success"
                            : orderStatus === "paid"
                              ? "badge-warning"
                              : "badge-info"
                        }`}
                      >
                        {orderStatus.charAt(0).toUpperCase() +
                          orderStatus.slice(1)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="success-actions">
                  <div className="order-paybox">
                    <button
                      type="button"
                      className="continue-btn"
                      onClick={handlePaymentClick}
                      disabled={startingPayment || !orderResult.invoiceId}
                    >
                      {startingPayment ? "Opening..." : "Pay Now"}
                    </button>
                    <PaymentModeDropdown
                      value={paymentProvider}
                      onChange={setPaymentProvider}
                      disabled={startingPayment || !orderResult.invoiceId}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Order Form with Two-Column Layout */
            <div className="order-content-wrapper">
              {/* Main Configuration Section */}
              <div className="order-main-section">
                <form onSubmit={handleSubmit}>
                  <div className="config-content">
                    {/* Space Name */}
                    <div className="config-section">
                      <div className="section-header">
                        <h3 className="section-title">
                          Space Name
                          <span style={{ color: "#ef4444", marginLeft: "4px" }}>
                            *
                          </span>
                        </h3>
                        <p className="section-description">
                          Choose a unique name for your Space. This will be used
                          in the endpoint URL.
                        </p>
                      </div>
                      <div className="space-name-input">
                        <input
                          type="text"
                          className="form-control"
                          value={formData.name}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              name: e.target.value.toLowerCase(),
                            })
                          }
                          placeholder="my-space"
                          required
                        />
                        <p className="section-hint">
                          Must be 8-63 characters, lowercase letters, numbers,
                          and hyphens only. Must be globally unique.
                        </p>
                      </div>
                    </div>

                    {/* Region Selection */}
                    <div className="config-section">
                      <div className="section-header">
                        <h3 className="section-title">
                          Region
                          <span style={{ color: "#ef4444", marginLeft: "4px" }}>
                            *
                          </span>
                        </h3>
                        <p className="section-description">
                          Choose the datacenter location closest to your users
                          for best performance.
                        </p>
                      </div>
                      <div className="region-grid">
                        {regions.map((region) => (
                          <div
                            key={region.slug}
                            className={`region-card ${
                              formData.region === region.slug ? "selected" : ""
                            }`}
                            onClick={() =>
                              setFormData({ ...formData, region: region.slug })
                            }
                          >
                            <div className="region-icon-wrapper">
                              {getRegionIcon(region.slug)}
                            </div>
                            <div className="region-info">
                              <div className="region-name">{region.name}</div>
                              <div className="region-slug">{region.slug}</div>
                            </div>
                            {formData.region === region.slug && (
                              <div className="region-checkmark">
                                <HiCheck />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Storage Tier */}
                    <div className="config-section">
                      <div className="section-header">
                        <h3 className="section-title">Storage Tier</h3>
                        <p className="section-description">
                          Select your storage capacity. You can upgrade anytime.
                        </p>
                      </div>
                      {loading && storageTiers.length === 0 ? (
                        <div className="text-muted">Loading pricing...</div>
                      ) : storageTiers.length === 0 ? (
                        <div className="text-muted">
                          No storage tiers available
                        </div>
                      ) : (
                        <div className="tier-grid">
                          {storageTiers.map((tier) => {
                            const currencySymbol = getCurrencySymbol();
                            return (
                              <div
                                key={tier.size_gb}
                                className={`tier-card ${
                                  formData.storage_tier === `${tier.size_gb}GB`
                                    ? "selected"
                                    : ""
                                }`}
                                onClick={() =>
                                  setFormData({
                                    ...formData,
                                    storage_tier: `${tier.size_gb}GB`,
                                  })
                                }
                              >
                                <div className="tier-size">{tier.label}</div>
                                <div className="tier-price">
                                  {currencySymbol}
                                  {tier.price.toFixed(2)}
                                  <span>/mo</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Access Control */}
                    <div className="config-section">
                      <div className="section-header">
                        <h3 className="section-title">Access Control</h3>
                        <p className="section-description">
                          Set default permissions for files in your Space.
                        </p>
                      </div>
                      <div className="acl-grid">
                        <label className="acl-option">
                          <input
                            type="radio"
                            name="acl"
                            value="private"
                            checked={formData.acl === "private"}
                            onChange={(e) =>
                              setFormData({ ...formData, acl: e.target.value })
                            }
                          />
                          <div className="acl-card">
                            <div className="acl-icon">
                              <HiLockClosed />
                            </div>
                            <div className="acl-details">
                              <div className="acl-title">Private</div>
                              <div className="acl-description">
                                Only you can access files
                              </div>
                            </div>
                          </div>
                        </label>
                        <label className="acl-option">
                          <input
                            type="radio"
                            name="acl"
                            value="public-read"
                            checked={formData.acl === "public-read"}
                            onChange={(e) =>
                              setFormData({ ...formData, acl: e.target.value })
                            }
                          />
                          <div className="acl-card">
                            <div className="acl-icon">
                              <HiGlobeAlt />
                            </div>
                            <div className="acl-details">
                              <div className="acl-title">Public Read</div>
                              <div className="acl-description">
                                Anyone can read files
                              </div>
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              {/* Sidebar - Order Summary */}
              <div className="order-sidebar">
                <div className="sidebar-card">
                  <h3 className="sidebar-title">Order Summary</h3>
                  <div className="sidebar-content">
                    <div className="summary-row">
                      <span className="summary-label">Space Name</span>
                      <span className="summary-value">
                        {formData.name || "—"}
                      </span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Region</span>
                      <span className="summary-value">
                        {formData.region
                          ? regions.find((r) => r.slug === formData.region)
                              ?.name || formData.region
                          : "—"}
                      </span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Storage</span>
                      <span className="summary-value">
                        {formData.storage_tier || "—"}
                      </span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Access</span>
                      <span className="summary-value">
                        {formData.acl === "private" ? "Private" : "Public Read"}
                      </span>
                    </div>
                    <div className="summary-divider" />
                    <div className="summary-total">
                      <div className="summary-row">
                        <span className="summary-label">Total</span>
                        <span className="summary-value">
                          {getSelectedTier()
                            ? `${getCurrencySymbol()}${getSelectedTier().price.toFixed(
                                2,
                              )}/mo`
                            : "—"}
                        </span>
                      </div>
                    </div>
                  </div>
                  {user ? (
                    <div className="sidebar-footer">
                      <button
                        type="button"
                        className="continue-btn"
                        onClick={handleSubmit}
                        disabled={submitting || !isFormValid()}
                      >
                        {submitting ? (
                          <>
                            <span className="spinner mx-2"></span>
                            Creating...
                          </>
                        ) : (
                          "Create Space"
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="sidebar-footer">
                      <div className="sidebar-login-message">
                        <p className="sidebar-login-text">
                          Log in to place your order. Explore regions and
                          pricing, then{" "}
                          <button
                            type="button"
                            className="sidebar-login-link"
                            onClick={handleLoginRedirect}
                          >
                            log in to continue
                          </button>
                          .
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Container>
      </div>
    </>
  );
};

export default OrderSpace;
